import { Check, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { VideoGallery } from "./VideoGallery";
import { CHRIST_VIDEOS } from "../data/christVideos";
import { scrollToSection } from "../lib/scroll";

const PRODUCTS = [
    {
        title: "Portali di Lavaggio Automatici",
        desc: "Gamma ALPHA, CADIS, VEGA, NEOX, FLUX e BIG BERTHA: portali modulari con design BLADE e asciugatura integrata.",
        img: "/christ/alpha.jpg",
    },
    {
        title: "Tunnel EVO STAR",
        desc: "Tunnel di lavaggio modulare per grandi volumi: fino a 120 veicoli all'ora a basso consumo energetico.",
        img: "/christ/evo-star.jpg",
    },
    {
        title: "Impianti Self-Service",
        desc: "Piste self-service BLADE e SKIP con carpenteria in acciaio, predisposte per il fotovoltaico.",
        img: "/christ/self-service.jpg",
    },
    {
        title: "Lavaggio Mezzi Pesanti",
        desc: "Impianti NOVA e SUPERNOVA completamente automatici per camion, autobus e flotte commerciali.",
        img: "/christ/camion.jpg",
    },
];

const ADVANTAGES = [
    "Garanzia ufficiale Christ Wash Systems",
    "Pronto intervento tecnico in 48–72 ore",
    "Ricambi originali",
    "Formazione certificata del personale",
    "Progettazione chiavi in mano dell'impianto",
];

export const ChristSection = () => (
    <section
        id="christ"
        data-testid="christ-wash-section"
        className="relative overflow-hidden bg-[#F2F5F9] py-20 lg:py-28"
    >
        <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 select-none font-display text-[16rem] font-black leading-none text-[#0B1F3A]/[0.03]"
        >
            1963
        </div>

        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
            <div className="grid gap-12 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <Reveal>
                        <span className="block text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]">
                            Settore 01 · Autolavaggio
                        </span>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <span
                            data-testid="christ-eyebrow"
                            className="mt-5 inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white py-1.5 pl-5 pr-1.5 shadow-[0_4px_16px_-6px_rgba(11,31,58,0.1)]"
                        >
                            <img
                                src="/brands/christ-logo.png"
                                alt="Christ Wash Systems"
                                className="h-6 w-auto"
                            />
                            <span className="rounded-full bg-[#E30613] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white">
                                Concessionario Ufficiale
                            </span>
                        </span>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="font-display mt-6 text-3xl font-extrabold tracking-tight text-[#0B1F3A] sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]">
                            Eccellenza tedesca nel lavaggio,{" "}
                            <span className="text-[#E30613]">dal 1963.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <p className="mt-6 text-base leading-relaxed text-[#475569] sm:text-lg">
                            Otto Christ AG è il costruttore europeo di riferimento per gli impianti
                            di autolavaggio e le strutture di copertura esterne. FTM Group S.r.l. è il suo{" "}
                            <strong className="font-semibold text-[#0B1F3A]">
                                concessionario ufficiale per la Calabria
                            </strong>
                            : vendita, installazione, ricambi originali e assistenza tecnica
                            autorizzata, direttamente sul territorio.
                        </p>
                    </Reveal>
                    <Reveal delay={0.26}>
                        <ul className="mt-8 space-y-3.5" data-testid="christ-advantages">
                            {ADVANTAGES.map((a) => (
                                <li key={a} className="flex items-start gap-3">
                                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#E30613]/10">
                                        <Check className="h-3.5 w-3.5 text-[#E30613]" />
                                    </span>
                                    <span className="text-sm font-medium text-[#0B1F3A] sm:text-base">
                                        {a}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                    <Reveal delay={0.34}>
                        <button
                            data-testid="christ-cta-button"
                            onClick={() => scrollToSection("contatti")}
                            className="group mt-10 inline-flex items-center gap-2.5 rounded-full bg-[#0B1F3A] px-7 py-3.5 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:bg-[#132b4f] active:scale-95"
                        >
                            Parla con un nostro tecnico
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </Reveal>
                </div>

                <div className="lg:col-span-7">
                    <div className="grid gap-5 sm:grid-cols-2" data-testid="christ-products-grid">
                        {PRODUCTS.map((p, i) => (
                            <Reveal key={p.title} delay={0.1 + i * 0.08}>
                                <article
                                    data-testid={`christ-product-${i}`}
                                    onClick={() => scrollToSection("schede-christ")}
                                    className="group relative h-full cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_-5px_rgba(11,31,58,0.08)] transition-[box-shadow,transform] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_48px_-12px_rgba(11,31,58,0.18)]"
                                >
                                    <div className="h-44 overflow-hidden">
                                        <img
                                            src={p.img}
                                            alt={p.title}
                                            loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                    </div>
                                    <div className="p-6">
                                        <h3 className="font-display text-lg font-bold text-[#0B1F3A]">
                                            {p.title}
                                        </h3>
                                        <p className="mt-2 text-sm leading-relaxed text-[#4F5D70]">
                                            {p.desc}
                                        </p>
                                    </div>
                                    <span className="absolute left-0 top-0 h-1 w-full origin-left scale-x-0 bg-[#E30613] transition-transform duration-500 group-hover:scale-x-100" />
                                </article>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.3}>
                        <div
                            data-testid="christ-ricambi-strip"
                            className="mt-5 flex flex-col items-start justify-between gap-4 rounded-2xl bg-[#0B1F3A] p-6 sm:flex-row sm:items-center"
                        >
                            <div>
                                <div className="font-display text-lg font-bold text-white">
                                    Assistenza e ricambi originali
                                </div>
                                <div className="mt-1 text-sm text-white/70">
                                    Ricambi originali Christ e tecnici autorizzati: fermi impianto
                                    ridotti al minimo.
                                </div>
                            </div>
                            <button
                                data-testid="christ-ricambi-cta"
                                onClick={() => scrollToSection("assistenza")}
                                className="shrink-0 rounded-full bg-[#E30613] px-6 py-3 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#C20410]"
                            >
                                Servizio Assistenza
                            </button>
                        </div>
                    </Reveal>
                </div>
            </div>
            <VideoGallery
                testid="christ-video-block"
                eyebrow="Gli impianti in azione"
                title="Guarda i portali Christ mentre lavorano."
                desc="Video dal canale YouTube ufficiale Christ Wash Systems: scegli l'impianto e premi play."
                brand="Christ Wash Systems"
                videos={CHRIST_VIDEOS}
            />
        </div>
    </section>
);
